import { createFileRoute, Link } from "@tanstack/react-router";
import { PenLine, Quote, ShieldCheck, Sparkles } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { SiteHeader } from "@/components/SiteHeader";
const FAQS = [
  {
    q: "Can you use AI to help with college essays?",
    a: "Yes — SmartEssay is designed to help with college, high-school, and graduate essays. It gives you a framed outline with real cited quotes so your ideas, analysis, and voice stay front and center. You still do the thinking; the AI handles the scaffolding.",
  },
  {
    q: "Is SmartEssay a free AI essay writer?",
    a: "You can try the Essay Reviewer and outline generator for free. Paid plans unlock unlimited outlines, more evidence per claim, MLA and APA 7 PDF exports, and saved history.",
  },
  {
    q: "Will my teacher know I used an AI essay writer?",
    a: "SmartEssay is built to scaffold, not to ghostwrite. Because the outline is a frame you fill in with your own analysis and voice, the finished essay sounds like you. We also publish an academic integrity policy you can share with your teacher.",
  },
  {
    q: "Does the essay writer include real citations?",
    a: "Yes. Every quote is a real word-for-word passage with a proper MLA or APA 7 citation. You can export the outline as a clean PDF with the citations formatted correctly.",
  },
];

export const Route = createFileRoute("/essay-writer")({
  head: () => ({
    meta: [
      { title: "AI Essay Writer with Real Quotes & Citations — SmartEssay" },
      { name: "description", content: "A free AI essay writer that builds a framed outline with real word-for-word quotes and MLA or APA 7 citations. Keep your voice; skip the blank page." },
      { property: "og:title", content: "AI Essay Writer with Real Quotes & Citations — SmartEssay" },
      { property: "og:description", content: "Framed outlines with real cited quotes. Keep your voice; skip the blank page." },
      { property: "og:url", content: "https://smartessay.ai/essay-writer" },
    ],
    links: [{ rel: "canonical", href: "https://smartessay.ai/essay-writer" }],
    scripts: [{
      type: "application/ld+json",
      children: JSON.stringify({
        "@context": "https://schema.org",
        "@type": "FAQPage",
        mainEntity: FAQS.map((f) => ({
          "@type": "Question",
          name: f.q,
          acceptedAnswer: { "@type": "Answer", text: f.a },
        })),
      }),
    }],
  }),
  component: EssayWriterPage,
});

function EssayWriterPage() {
  return (
    <div className="min-h-screen bg-hero-gradient">
      <SiteHeader />
      <main className="mx-auto max-w-3xl px-6 pb-24">
        <header className="text-center">
          <p className="text-xs font-medium uppercase tracking-[0.22em] text-primary">AI essay writer</p>
          <h1 className="mt-3 font-display text-4xl font-medium leading-tight tracking-tight sm:text-5xl">
            An <em>AI essay writer</em> that keeps your voice
          </h1>
          <p className="mx-auto mt-4 max-w-xl text-muted-foreground">
            SmartEssay is an AI essay writer for students who want help with structure — not a ghostwriter. Drop in a title and get a framed outline with real word-for-word quotes and MLA or APA 7 citations. You bring the thinking; we handle the scaffolding.
          </p>
          <div className="mt-6 flex flex-wrap justify-center gap-3">
            <Button asChild size="lg" className="shadow-glow">
              <Link to="/generator">Write my essay outline</Link>
            </Button>
            <Button asChild size="lg" variant="outline">
              <Link to="/help">See how it works</Link>
            </Button>
          </div>
        </header>

        <section className="mt-14 grid gap-6 sm:grid-cols-3">
          {[
            { icon: PenLine, title: "Framed outline", body: "Sentence frames you fill in — you do the analysis, we handle the structure." },
            { icon: Quote, title: "Real citations", body: "Word-for-word quotes with proper MLA or APA 7 formatting every time." },
            { icon: Sparkles, title: "Your voice", body: "Voice matching adapts to how you write so the finished essay sounds like you." },
          ].map((f) => (
            <div key={f.title} className="rounded-2xl border border-border bg-card p-5 shadow-soft">
              <f.icon className="h-5 w-5 text-primary" />
              <h2 className="mt-3 font-display text-base font-semibold">{f.title}</h2>
              <p className="mt-2 text-sm text-muted-foreground">{f.body}</p>
            </div>
          ))}
        </section>

        <section className="mt-14">
          <h2 className="font-display text-2xl font-medium">Frequently asked questions</h2>
          <Accordion type="single" collapsible className="mt-4">
            {FAQS.map((f, i) => (
              <AccordionItem key={i} value={`q-${i}`}>
                <AccordionTrigger className="text-left">{f.q}</AccordionTrigger>
                <AccordionContent className="text-muted-foreground">{f.a}</AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        </section>

        <section className="mt-14 rounded-2xl border border-border bg-card p-8 text-center shadow-soft">
          <ShieldCheck className="mx-auto h-6 w-6 text-primary" />
          <h2 className="mt-3 font-display text-xl font-semibold">Try the essay writer free</h2>
          <p className="mt-2 text-sm text-muted-foreground">No card needed to start your first outline.</p>
          <Button asChild className="mt-5 shadow-glow">
            <Link to="/generator">Get started</Link>
          </Button>
        </section>
      </main>
    </div>
  );
}
